<template>
  <div class="banner-hero critical-component-clear">
    <div class="banner-hero__image">
      <responsive-image
        :image="data.image"
        :lazyload="data.lazyLoad"
        block-class="banner-hero"
        min-max="2048-4096"
        :ratio="false"
      />

      <div class="banner-hero__overlay" />

      <component
        :is="liquid.sectionIndex === 0 ? 'h1' : 'h2'"
        class="banner-hero__title text-heading-5xl-desktop text-heading-3xl"
        :class="`banner-hero__title-${data.titleColor}`"
        :style="{'color':data.titleColor}"
      >
        <span v-text="data.title" />
      </component>
    </div>
  </div>
</template>

<script>

/**
 * Vue: banner-hero (banner-hero)
 * -----------------------------------------------------------------------------
 * Custom Landing Page Hero Section Banner.
 *
 * @param {String} data.title - Section title.
 * @param {String} data.titleColor - Section title color
 * @param {Object} data.image - Section image objec
 * @param {Number} liquid.sectionIndex - Section's 0 based index.
 *
 */

import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'BannerHero',

  components: {
    ResponsiveImage,
  },

  props: {
    data: {
      type: Object,
      default: () => ({
        title: '',
        titleColor: 'black',
        lazyLoad: true,
        image: {
          src: '',
          alt: '',
        },
      }),
    },

    liquid: {
      type: Object,
      default: () => ({
        sectionIndex: 0,
      }),
    },
  },

  data() {
    return {
      colorBreakpointCount: 8,
      elementTopExtraSpace: 20,
      elements: {
        image: '',
        title: '',
        parentElement: '',
      },
      headerHeight: 0,
      maxPaddingBottom: 257,
      minPaddingBottom: 50,
      titleMarginTop: 0,
      titleMarginTopStep: 2,
      textTransitionColors: ['#dcdcdc', '#c8c8c8', '#b4b4b4', '#a0a0a0', '#8c8c8c', '#787878', '#646464', 'black'],
    }
  },

  mounted() {
    this.elements.image = this.$el.querySelector('.banner-hero__image')
    this.elements.title = this.$el.querySelector('.banner-hero__title')
    this.elements.parentElement = document.querySelector('.banner-hero.critical-component-clear')
    this.headerHeight = document.querySelector('.site-header-container').getBoundingClientRect().height
    this.maxPaddingBottom = this.isMobile() ? 141 : 257
    this.titleMarginTopStep = this.isMobile() ? -1.8 : -1.5

    this.listenScrollEvent()
  },

  methods: {

    /**
     * Listens to page scroll and executes animations
     * if the element is on correct location
     */
    listenScrollEvent() {
      window.addEventListener('scroll', () => {
        const elementPositionData = this.elements.image.getBoundingClientRect()
        const elementIsInsideViewPort =
          elementPositionData.bottom > (this.headerHeight + this.elementTopExtraSpace)
        const elementReachedTop =
          this.headerHeight + this.elementTopExtraSpace > elementPositionData.top
        const colorBreakPoints = elementPositionData.height / this.colorBreakpointCount

        if (elementReachedTop && elementIsInsideViewPort) {
          this.executeAnimations(elementPositionData, colorBreakPoints)
        } else if (!elementReachedTop) {
          this.resetWhenOnTop()
        } else if (!elementIsInsideViewPort) {
          this.resetWhenGoneFromScreen()
        }
      })
    },

    /**
     * Handles animations once image is above the viewport,
     * resets values otherwise
     *
     * @param {Object} elementPositionData - On screen position data of image.
     * @param {Number} colorBreakPoints - Value for color changing breakpoints.
     *
     */
    executeAnimations(elementPositionData, colorBreakPoints) {
      if (elementPositionData.top < 0) {
        this.executeTitleMovementAnimation(elementPositionData)
        this.executeBottomPaddingAnimation(elementPositionData)
        this.executeColorChangingAnimation(elementPositionData, colorBreakPoints)

      } else {
        this.elements.title.style.marginTop = `0px`
        this.elements.parentElement.style.paddingBottom = `50px`
        this.elements.title.style.color = this.data.titleColor
      }
    },

    /**
     * Moving Title to bottom on scroll
     *
     * @param {Object} elementPositionData - On screen position data of image.
     *
     */
    executeTitleMovementAnimation(elementPositionData) {
      this.elements.title.style.marginTop = `${elementPositionData.top * this.titleMarginTopStep}px`
    },

    /**
     * Increase bottom padding on scroll
     *
     * @param {Object} elementPositionData - On screen position data of image.
     *
     */
    executeBottomPaddingAnimation(elementPositionData) {
      if (elementPositionData.top / -2 <= this.maxPaddingBottom &&
        elementPositionData.top / -2 >= this.minPaddingBottom) {
        this.elements.parentElement.style.paddingBottom = `${elementPositionData.top / -2}px`
      }
    },

    /**
     * Changing color from white to black on scroll, works only
     * when selected title color is white
     *
     * @param {Object} elementPositionData - On screen position data of image.
     * @param {Number} colorBreakPoints - Value for color changing breakpoints.
     *
     */
    executeColorChangingAnimation(elementPositionData, colorBreakPoints) {
      if (this.data.titleColor === 'white') {
        if (elementPositionData.top + elementPositionData.height < colorBreakPoints) {
          this.elements.title.style.color = this.textTransitionColors[7]
        } else if (elementPositionData.top + elementPositionData.height < colorBreakPoints * 2) {
          this.elements.title.style.color = this.textTransitionColors[6]
        } else if (elementPositionData.top + elementPositionData.height < colorBreakPoints * 3) {
          this.elements.title.style.color = this.textTransitionColors[5]
        } else if (elementPositionData.top + elementPositionData.height < colorBreakPoints * 4) {
          this.elements.title.style.color = this.textTransitionColors[4]
        } else if (elementPositionData.top + elementPositionData.height < colorBreakPoints * 5) {
          this.elements.title.style.color = this.textTransitionColors[3]
        } else if (elementPositionData.top + elementPositionData.height < colorBreakPoints * 6) {
          this.elements.title.style.color = this.textTransitionColors[2]
        } else if (elementPositionData.top + elementPositionData.height < colorBreakPoints * 7) {
          this.elements.title.style.color = this.textTransitionColors[1]
        } else if (elementPositionData.top + elementPositionData.height < colorBreakPoints * 8) {
          this.elements.title.style.color = this.textTransitionColors[0]
        }
      }
    },

    /**
     * Resetting element CSS when reached top
     */
    resetWhenOnTop() {
      this.elements.title.style.marginTop = `0px`
      this.elements.title.style.color = this.data.titleColor
    },

    /**
     * Resetting element CSS when gone from ViewPort
     */
    resetWhenGoneFromScreen() {
      this.elements.title.style.color = 'black'
    },

    /**
     * Check if mobile device
     */
    isMobile() {
      if (screen.width <= 768) {
        return true
      }

      return false

    },
  },
}
</script>

<style lang="scss">
@import './banner-hero';
</style>
